import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  Stack,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  usePartsDashboard,
  useDismissPartNeed,
  useRestorePartNeed,
  useReservePartForWorkOrder,
  useCreatePurchaseOrder,
  useSuppliers,
  type PartsDashboardFilters,
  type PartsDashboardItem,
  type PartsDashboardSummary,
  type PartNeedStatus,
} from '@/hooks/useInventory';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { RegistrationPlate } from '@/components/common';
import { ApiError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/utils/locale';
import {
  PartNeedDismissDialog,
  PartNeedReserveDialog,
  CreatePurchaseOrderFromNeedsDialog,
} from '@/components/inventory';
import { useOpenWorkOrder } from '@/hooks/useWorkOrderWindow';
import { useUrlSearchParam } from '@/hooks/useUrlSearchParam';

// `error` keeps backordered distinct from arrived (`warning`); `secondary` is reserved (#3567).
const statusColorMap: Record<PartNeedStatus, 'default' | 'secondary' | 'info' | 'warning' | 'error' | 'success'> = {
  needed: 'default',
  reserved: 'secondary',
  ordered: 'info',
  backordered: 'error',
  arrived: 'warning',
  installed: 'success',
  dismissed: 'default',
};

const statusKeyMap: Record<PartNeedStatus, string> = {
  needed: 'partsDashboard.statusNeeded',
  reserved: 'partsDashboard.statusReserved',
  ordered: 'partsDashboard.statusOrdered',
  backordered: 'partsDashboard.statusBackordered',
  arrived: 'partsDashboard.statusArrived',
  installed: 'partsDashboard.statusInstalled',
  dismissed: 'partsDashboard.statusDismissed',
};

const ALL_STATUSES: PartNeedStatus[] = ['needed', 'reserved', 'ordered', 'backordered', 'arrived', 'installed', 'dismissed'];

const formatCents = (cents: number) => `${(cents / 100).toFixed(2)} €`;

/** Only needs with a resolved catalog part can be reserved or added to a PO. */
const isOrderable = (item: PartsDashboardItem) =>
  item.partId !== null && (item.status === 'needed' || item.status === 'reserved');

export default function PartsDashboardPage() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const { t: tWorkorders } = useTranslation('workorders');
  const openWorkOrder = useOpenWorkOrder();
  const notify = useNotify();
  const canCreatePurchaseOrder = useAuthStore((s) => s.hasPermission('purchase_orders.create'));

  // Search and status live in the URL so Back restores them (#4100).
  // Prefixed: the inventory page around this tab owns `q` and `filter`.
  const [searchQuery, setSearchQuery] = useUrlSearchParam('needsQ');
  const [statusParam, setStatusParam] = useUrlSearchParam('needsStatus', { immediate: true });
  const statusFilter: PartNeedStatus | 'all' = (ALL_STATUSES as readonly string[]).includes(statusParam)
    ? (statusParam as PartNeedStatus)
    : 'all';
  const setStatusFilter = (next: PartNeedStatus | 'all') => setStatusParam(next === 'all' ? '' : next);
  const [mechanicFilter, setMechanicFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'unavailable'>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [dismissTarget, setDismissTarget] = useState<PartsDashboardItem | null>(null);
  const [reserveTarget, setReserveTarget] = useState<PartsDashboardItem | null>(null);
  const [createPoNeeds, setCreatePoNeeds] = useState<PartsDashboardItem[] | null>(null);

  const filters: PartsDashboardFilters = useMemo(() => ({
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(searchQuery && { q: searchQuery }),
    ...(mechanicFilter && { mechanicId: mechanicFilter }),
    ...(supplierFilter && { supplierId: supplierFilter }),
    ...(availabilityFilter !== 'all' && { availability: availabilityFilter }),
  }), [statusFilter, searchQuery, mechanicFilter, supplierFilter, availabilityFilter]);

  const { data, isLoading } = usePartsDashboard(filters);
  const mechanicsQuery = useMechanics();
  const suppliersQuery = useSuppliers({ isActive: true });
  const suppliers = useMemo(
    () => (suppliersQuery.data?.pages ?? []).flatMap((page) => page.data),
    [suppliersQuery.data],
  );

  const dismissMutation = useDismissPartNeed();
  const restoreMutation = useRestorePartNeed();
  const reserveMutation = useReservePartForWorkOrder();
  const createPoMutation = useCreatePurchaseOrder();

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const summary: PartsDashboardSummary = data?.summary ?? {
    totalNeeded: 0, totalReserved: 0, totalOrdered: 0, totalArrived: 0, totalInstalled: 0, totalDismissed: 0,
    totalValueCents: 0,
  };

  const orderableItems = useMemo(() => items.filter(isOrderable), [items]);
  const selectedItems = useMemo(() => items.filter((i) => selectedIds.includes(i.id)), [items, selectedIds]);
  const allOrderableSelected = orderableItems.length > 0 && orderableItems.every((i) => selectedIds.includes(i.id));

  const toggleSelectAll = () => {
    setSelectedIds(allOrderableSelected ? [] : orderableItems.map((i) => i.id));
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleDismiss = (reason: string) => {
    if (!dismissTarget) return;
    dismissMutation.mutate(
      { id: dismissTarget.id, reason },
      {
        onSuccess: () => {
          notify.success(t('partsDashboard.dismiss.success'));
          setDismissTarget(null);
        },
        onError: () => notify.error(tc('error'), t('partsDashboard.dismiss.error')),
      },
    );
  };

  const handleRestore = (item: PartsDashboardItem) => {
    restoreMutation.mutate(item.id, {
      onSuccess: () => notify.success(t('partsDashboard.restoreSuccess')),
      onError: () => notify.error(tc('error'), t('partsDashboard.restoreError')),
    });
  };

  const handleReserve = (quantity: number) => {
    if (!reserveTarget?.partSku) return;
    reserveMutation.mutate(
      { bookingId: reserveTarget.bookingId, partNumber: reserveTarget.partSku, quantity },
      {
        onSuccess: () => {
          notify.success(t('partsDashboard.reserve.success'));
          setReserveTarget(null);
        },
        onError: (error) => {
          const reason = error instanceof ApiError && error.data && typeof error.data === 'object'
            ? (error.data as { reason?: string }).reason
            : undefined;
          notify.error(
            tc('error'),
            reason === 'insufficient_stock' ? t('partsDashboard.reserve.insufficientStock') : t('partsDashboard.reserve.error'),
          );
        },
      },
    );
  };

  const handleCreatePo = (poData: {
    supplierId: string;
    warehouseId: string;
    lines: { partId: string; quantityOrdered: number; unitPriceCents: number; linkedBookingId: string }[];
  }) => {
    createPoMutation.mutate(poData, {
      onSuccess: (result) => {
        notify.success(t('partsDashboard.createPo.success', { orderNumber: result.orderNumber }));
        setCreatePoNeeds(null);
        setSelectedIds([]);
      },
      onError: () => notify.error(tc('error'), t('partsDashboard.createPo.errorGeneric')),
    });
  };

  // Hand-set order status outranks the purchase order. The chip tooltip records
  // that a person set it; the caption shows the PO status when they differ (#3568).
  const renderOrderStatus = (item: PartsDashboardItem) => {
    const chip = (
      <Chip
        size="small"
        color={statusColorMap[item.status]}
        variant={item.status === 'dismissed' ? 'outlined' : 'filled'}
        label={t(statusKeyMap[item.status])}
      />
    );
    const purchaseOrderStatus = item.purchaseOrderStatus;
    const setByHandDate = item.orderStatusSetAt ? formatDateTime(item.orderStatusSetAt) : '';
    const setByHandLabel = `${t('partsDashboard.setByHand', { date: setByHandDate })}`;
    const tooltipTitle = item.orderStatus == null
      ? null
      : (setByHandDate ? setByHandLabel : setByHandLabel.trimEnd());

    return (
      <>
        {tooltipTitle != null ? (
          <Tooltip title={tooltipTitle}>
            {chip}
          </Tooltip>
        ) : (
          chip
        )}
        {item.orderStatus != null && purchaseOrderStatus != null && purchaseOrderStatus !== item.status && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t('partsDashboard.purchaseOrderHint', { status: t(statusKeyMap[purchaseOrderStatus]) })}
          </Typography>
        )}
        {item.status === 'dismissed' && item.dismissedReason && (
          <Tooltip title={item.dismissedReason}>
            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 140 }}>
              {item.dismissedReason}
            </Typography>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {t('partsDashboard.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('partsDashboard.description')}
      </Typography>

      {/* Summary cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 130, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalNeeded')}</Typography>
            <Typography variant="h5" fontWeight={600}>{summary.totalNeeded}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 130, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalReserved')}</Typography>
            <Typography variant="h5" fontWeight={600} color="secondary.main">{summary.totalReserved}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 130, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalOrdered')}</Typography>
            <Typography variant="h5" fontWeight={600} color="info.main">{summary.totalOrdered}</Typography>
          </CardContent>
        </Card>
        {typeof summary.totalBackordered === 'number' && (
          <Card sx={{ minWidth: 130, flex: 1 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalBackordered')}</Typography>
              <Typography variant="h5" fontWeight={600} color="error.main">{summary.totalBackordered}</Typography>
            </CardContent>
          </Card>
        )}
        <Card sx={{ minWidth: 130, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalArrived')}</Typography>
            <Typography variant="h5" fontWeight={600} color="warning.main">{summary.totalArrived}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 130, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalInstalled')}</Typography>
            <Typography variant="h5" fontWeight={600} color="success.main">{summary.totalInstalled}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 130, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('partsDashboard.totalValue')}</Typography>
            <Typography variant="h5" fontWeight={600}>{formatCents(summary.totalValueCents)}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        // Bottom-aligned: a label-less search field is 16px shorter than a
        // labelled one (the theme reserves that room above the box), so
        // centring leaves the search box floating above its neighbours (#2259).
        sx={{ mb: 2, alignItems: 'flex-end', flexWrap: 'wrap', rowGap: 1 }}
      >
        <TextField
          size="small"
          placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('partsDashboard.statusColumn')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('partsDashboard.statusColumn')}
            onChange={(e) => setStatusFilter(e.target.value as PartNeedStatus | 'all')}
          >
            <MenuItem value="all">{t('partsDashboard.filterAll')}</MenuItem>
            {ALL_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>{t(statusKeyMap[status])}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('partsDashboard.mechanicFilterLabel')}</InputLabel>
          <Select
            value={mechanicFilter}
            label={t('partsDashboard.mechanicFilterLabel')}
            onChange={(e) => setMechanicFilter(e.target.value)}
          >
            <MenuItem value="">{t('partsDashboard.filterAll')}</MenuItem>
            {(mechanicsQuery.data ?? []).map((mechanic) => (
              <MenuItem key={mechanic.id} value={mechanic.id}>{mechanic.firstName} {mechanic.lastName}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('partForm.supplierLabel')}</InputLabel>
          <Select
            value={supplierFilter}
            label={t('partForm.supplierLabel')}
            onChange={(e) => setSupplierFilter(e.target.value)}
          >
            <MenuItem value="">{t('partsDashboard.filterAll')}</MenuItem>
            {suppliers.map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('partsDashboard.availabilityFilterLabel')}</InputLabel>
          <Select
            value={availabilityFilter}
            label={t('partsDashboard.availabilityFilterLabel')}
            onChange={(e) => setAvailabilityFilter(e.target.value)}
          >
            <MenuItem value="all">{t('partsDashboard.filterAll')}</MenuItem>
            <MenuItem value="available">{t('partsDashboard.availabilityAvailable')}</MenuItem>
            <MenuItem value="unavailable">{t('partsDashboard.availabilityUnavailable')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Bulk action toolbar */}
      {selectedItems.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
          <Typography variant="body2">
            {t('partsDashboard.selectedCount', { count: selectedItems.length })}
          </Typography>
          {canCreatePurchaseOrder && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              onClick={() => setCreatePoNeeds(selectedItems)}
            >
              {t('partsDashboard.createPo.action')}
            </Button>
          )}
        </Stack>
      )}

      {/* Content */}
      {isLoading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={48} />)}
        </Stack>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <BuildIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">{t('partsDashboard.noItems')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {canCreatePurchaseOrder && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allOrderableSelected}
                      indeterminate={selectedIds.length > 0 && !allOrderableSelected}
                      onChange={toggleSelectAll}
                      disabled={orderableItems.length === 0}
                    />
                  </TableCell>
                )}
                <TableCell>{t('partsDashboard.partColumn')}</TableCell>
                <TableCell>{t('partsDashboard.workOrderColumn')}</TableCell>
                <TableCell>{t('partsDashboard.customerColumn')}</TableCell>
                <TableCell>{t('partsDashboard.vehicleColumn')}</TableCell>
                <TableCell align="right">{t('partsDashboard.quantityColumn')}</TableCell>
                <TableCell align="right">{t('partsDashboard.currentStockColumn')}</TableCell>
                <TableCell>{t('partsDashboard.statusColumn')}</TableCell>
                <TableCell>{t('partsDashboard.supplierColumn')}</TableCell>
                <TableCell>{t('partsDashboard.etaColumn')}</TableCell>
                <TableCell align="right">{t('partsDashboard.actionsColumn')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover selected={selectedIds.includes(item.id)}>
                  {canCreatePurchaseOrder && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelectOne(item.id)}
                        disabled={!isOrderable(item)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.partName}</Typography>
                    {item.partSku && (
                      <Typography variant="caption" color="text.secondary">{item.partSku}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={(e) => openWorkOrder(item.bookingId, e)}
                    >
                      {item.workOrderNumber ?? tWorkorders('noWorkOrderNumber')}
                    </Typography>
                    {item.mechanicName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.mechanicName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {item.customerName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.vehicleRegistration && (
                      <RegistrationPlate registrationNumber={item.vehicleRegistration} sx={{ height: 22 }} />
                    )}
                    {item.vehicleMakeModel && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.vehicleMakeModel}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">
                    {item.currentStock ?? '—'}
                  </TableCell>
                  <TableCell>
                    {renderOrderStatus(item)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {item.poInfo?.supplierName ?? '—'}
                    </Typography>
                    {item.poInfo && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.poInfo.poOrderNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {item.poInfo?.expectedDeliveryDate
                        ? formatDate(item.poInfo.expectedDeliveryDate)
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t('partsDashboard.actions.openWorkOrder')}>
                        <IconButton size="small" onClick={(e) => openWorkOrder(item.bookingId, e)} aria-label={t('partsDashboard.actions.openWorkOrder')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {item.status === 'needed' && item.partId !== null && (
                        <Tooltip title={t('partsDashboard.actions.reserve')}>
                          <IconButton size="small" onClick={() => setReserveTarget(item)} aria-label={t('partsDashboard.actions.reserve')}>
                            <Inventory2Icon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canCreatePurchaseOrder && isOrderable(item) && (
                        <Tooltip title={t('partsDashboard.actions.addToPo')}>
                          <IconButton size="small" onClick={() => setCreatePoNeeds([item])} aria-label={t('partsDashboard.actions.addToPo')}>
                            <ShoppingCartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {item.status === 'dismissed' ? (
                        <Tooltip title={t('partsDashboard.actions.restore')}>
                          <IconButton size="small" onClick={() => handleRestore(item)} aria-label={t('partsDashboard.actions.restore')}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : item.status !== 'installed' && (
                        <Tooltip title={t('partsDashboard.actions.dismiss')}>
                          <IconButton size="small" onClick={() => setDismissTarget(item)} aria-label={t('partsDashboard.actions.dismiss')}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PartNeedDismissDialog
        open={dismissTarget !== null}
        onClose={() => setDismissTarget(null)}
        partName={dismissTarget?.partName ?? ''}
        onDismiss={handleDismiss}
        isLoading={dismissMutation.isPending}
      />

      <PartNeedReserveDialog
        open={reserveTarget !== null}
        onClose={() => setReserveTarget(null)}
        partName={reserveTarget?.partName ?? ''}
        neededQuantity={reserveTarget?.quantity ?? 1}
        currentStock={reserveTarget?.currentStock ?? null}
        onReserve={handleReserve}
        isLoading={reserveMutation.isPending}
      />

      <CreatePurchaseOrderFromNeedsDialog
        open={createPoNeeds !== null}
        onClose={() => setCreatePoNeeds(null)}
        needs={createPoNeeds ?? []}
        onCreate={handleCreatePo}
        isLoading={createPoMutation.isPending}
      />
    </Box>
  );
}
